import React, { useState, useEffect, useContext } from 'react';
import {
  Avatar,
  Typography,
  IconButton,
  Box,
  makeStyles,
  Chip,
  Tooltip
} from '@material-ui/core';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  // ✅ Ícones de chamada comentados - manter para uso futuro
  // Videocam as VideoCallIcon,
  // Phone as PhoneIcon
} from '@material-ui/icons';
import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';

const useStyles = makeStyles((theme) => ({  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 64,
    height: 64, // ✅ Altura fixa para evitar sobreposição
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    position: 'relative', // ✅ Garantir que fique no topo
    zIndex: 10, // ✅ Ficar acima de outros elementos
    flexShrink: 0, // ✅ Não permitir encolhimento
  },
  chatInfo: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    marginLeft: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
      borderRadius: 8,
      padding: '4px 8px',
      margin: '0 -8px',
    }
  },
  avatar: {
    width: 40,
    height: 40,
    fontSize: '16px',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  chatDetails: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0, // Para permitir text overflow
  },
  chatTitle: {
    fontWeight: 500,
    fontSize: '16px',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatSubtitle: {
    fontSize: '13px',
    color: theme.palette.text.secondary,
    marginTop: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  closeButton: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
      color: theme.palette.text.primary,
    }
  },
  actionButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
      color: theme.palette.text.primary,
    }
  },
  onlineIndicator: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    marginRight: 4,
  },
  participantsChip: {
    height: 20,
    fontSize: '11px',
    backgroundColor: 'rgba(0,0,0,0.04)',
    '& .MuiChip-label': {
      paddingLeft: 6,
      paddingRight: 6,
    }
  },
  usersChipsRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    gap: 4,
    marginTop: 2,
    maxWidth: '100%',
  },
  userChip: {
    fontSize: 11,
    height: 18,
    fontWeight: 500,
    borderRadius: 9,
    marginRight: 4,
    flexShrink: 0,
    maxWidth: 120,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: 0,
  },
  userChipOnline: {
    backgroundColor: '#25D366',
    color: 'white',
  },
  userChipOffline: {
    backgroundColor: '#E0E0E0',
    color: '#757575',
  },
}));

const ChatHeader = ({ 
  chat, 
  onSearchClick, 
  onVideoCall, 
  onPhoneCall, 
  onMenuClick,
  onHeaderClick, // ✅ Nova prop para clique no cabeçalho
  onClose, // ✅ Nova prop para fechar chat
  isSearchOpen = false 
}) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;

  const [usersState, setUsersState] = useState(chat?.users || []);

  // Atualização em tempo real dos status dos usuários (igual MomentsUser)
  useEffect(() => {
    setUsersState(chat?.users || []);
  }, [chat]);

  useEffect(() => {
    if (!chat || !chat.users || !socket || !companyId) return;
    let isMounted = true;
    const fetchUserStatus = async () => {
      if (!isMounted) return;
      try {
        const { data } = await api.get("/users");
        const usersData = Array.isArray(data) ? data : (data.users || []);
        setUsersState(prevUsers => prevUsers.map(chatUser => {
          const updatedUser = usersData.find(u => u.id === chatUser.user?.id);
          return updatedUser 
            ? { ...chatUser, user: { ...chatUser.user, online: updatedUser.online } }
            : chatUser;
        }));
      } catch (err) {
        // Silencia erro
      }
    };
    fetchUserStatus();
    const interval = setInterval(fetchUserStatus, 6000);
    const onUserUpdate = (data) => {
      if (data.action === "update" && data.user) {
        setUsersState(prevUsers => prevUsers.map(chatUser => 
          chatUser.user?.id === data.user.id 
            ? { ...chatUser, user: { ...chatUser.user, online: data.user.online } }
            : chatUser
        ));
      }
    };
    socket.on(`company-${companyId}-user`, onUserUpdate);
    return () => {
      isMounted = false;
      clearInterval(interval);
      socket.off(`company-${companyId}-user`, onUserUpdate);
    };
  }, [chat, socket, companyId]);

  if (!chat || !chat.id) {
    return (
      <div className={classes.header}>
        <Typography variant="body1" color="textSecondary">
          Selecione um chat para começar
        </Typography>
      </div>
    );
  }

  // Gera avatar com iniciais
  const getAvatarText = (title) => {
    if (!title) return '?';
    const words = title.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return title.slice(0, 2).toUpperCase();
  };

  // Formata info dos participantes (apenas texto)
  const getParticipantsText = () => {
    if (!usersState || usersState.length === 0) return null;
    const totalUsers = usersState.length;
    return (
      <span style={{ color: '#888', fontSize: 13 }}>
        • {totalUsers} participante{totalUsers !== 1 ? 's' : ''}
      </span>
    );
  };

  // Chips dos participantes (abaixo do nome)
  const getParticipantsChips = () => {
    if (!usersState || usersState.length === 0) return null;
    // Ordena: online primeiro (alfabético), depois offline (alfabético)
    const sortedUsers = usersState.slice().sort((a, b) => {
      if ((a.user?.online && !b.user?.online)) return -1;
      if ((!a.user?.online && b.user?.online)) return 1;
      return (a.user?.name || '').localeCompare(b.user?.name || '');
    });
    return (
      <div className={classes.usersChipsRow} style={{ marginTop: 2 }}>
        {sortedUsers.map((chatUser, idx) => (
          <Chip
            key={chatUser.user?.id || idx}
            label={chatUser.user?.name || 'Usuário'}
            size="small"
            className={
              classes.userChip + ' ' + (chatUser.user?.online ? classes.userChipOnline : classes.userChipOffline)
            }
          />
        ))}
      </div>
    );
  };

  return (
    <div className={classes.header}>
      {/* ✅ Botão de fechar conversa */}
      {onClose && (
        <Tooltip title="Fechar conversa (ESC)">
          <IconButton 
            className={classes.closeButton}
            onClick={onClose}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
      )}
      <Avatar 
        className={classes.avatar}
        src={chat.avatar} // Se houver avatar customizado
      >
        {getAvatarText(chat.title)}
      </Avatar>
      <div className={classes.chatInfo} onClick={onHeaderClick}>
        <div className={classes.chatDetails} style={{ minWidth: 0 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'nowrap', minWidth: 0 }}>
            <Typography className={classes.chatTitle} style={{ marginRight: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
              {chat.title || 'Chat sem título'}
            </Typography>
            {getParticipantsText()}
          </Box>
          {getParticipantsChips()}
        </div>
      </div>
      <div className={classes.actionButtons}>
        <Tooltip title="Buscar mensagens">
          <IconButton 
            className={classes.actionButton}
            onClick={onSearchClick}
            size="small"
            style={{ 
              backgroundColor: isSearchOpen ? 'rgba(0,0,0,0.08)' : 'transparent',
              color: isSearchOpen ? '#1976d2' : 'inherit'
            }}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>        {/* 
        // ✅ Ícones de chamada removidos temporariamente - manter para uso futuro
        <Tooltip title="Chamada de vídeo">
          <IconButton 
            className={classes.actionButton}
            onClick={onVideoCall}
            size="small"
          >
            <VideoCallIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Chamada de voz">
          <IconButton 
            className={classes.actionButton}
            onClick={onPhoneCall}
            size="small"
          >
            <PhoneIcon />
          </IconButton>
        </Tooltip>
        */}

        {/* 
        // ✅ Botão "Mais opções" removido temporariamente - manter para uso futuro
        <Tooltip title="Mais opções">
          <IconButton 
            className={classes.actionButton}
            onClick={onMenuClick}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
        */}
      </div>
    </div>
  );
};

export default ChatHeader;
